import axios from '@/services/axios'

const defaultState = () => {
  return {
    supportedCities: [],
    wallet: {},
    walletHistory: [],
    busStops: [],
    bookings: [],
    activeBookings: [],
    pastBookings: [],
    notifications: [],
    unreadNotificationsCount: 0,
    walletSource: 'Personal',
    localBookingData: null
  }
}
const state = defaultState()

const getters = {
  supportedCities: state => state.supportedCities,
  wallet: state => state.wallet,
  walletHistory: state => state.walletHistory,
  busStops: state => state.busStops,
  bookings: state => [...state.activeBookings, ...state.pastBookings],
  activeBookings: state => state.activeBookings,
  pastBookings: state => state.pastBookings,
  notifications: state => state.notifications,
  unreadNotificationsCount: state => state.unreadNotificationsCount,
  localBookingData: state => state.localBookingData
}

const actions = {
  async init({ dispatch }) {
    dispatch('fetchSupportedCities')
    dispatch('fetchBusStops')
    dispatch('fetchUnreadNotificationsCount')
  },
  fetchBusStops({ commit }) {
    axios
      .get('/bus-stops')
      .then(res => {
        commit('setBusStops', res.data)
      })
      .catch(() => {
        //
      })
  },
  fetchSupportedCities({ commit }) {
    axios
      .get('/city', { params: { 'fields[city]': 'id,name' } })
      .then(res => {
        commit('setSupportedCities', res.data.data)
      })
      .catch(() => {
        //
      })
  },
  async fetchWallet({ commit, dispatch }) {
    const userId = localStorage.getItem('__user_id')

    if (!userId) {
      return Promise.reject()
    }

    return axios
      .get(`/users/${userId}/wallet`)
      .then(res => {
        commit('setWallet', res.data.wallet)
        commit('setWalletHistory', res.data.walletHistory)

        dispatch('fetchWalletAccountNumbers')
      })
      .catch(err => {
        Promise.reject(err)
      })
  },
  async fetchWalletAccountNumbers({ state, commit }) {
    try {
      const wallet = state.wallet
      const response = await axios.get(`/wallets/${wallet.id}/account-numbers`)

      // The account numbers might not be available on first try.
      // If they are not, create
      // end if still not available after a second try.
      if (response.data.length === 0) {
        axios.post(`/wallets/${wallet.id}/account-numbers`, {provider: 'payed'})
            .then(res => {
              console.log({res})
              if (!res.data) {
                return Promise.reject()
              } else {
                commit('setWallet', {
                  ...wallet,
                  accountNumbers: [res.data]
                })
              }
            })
            .catch(err => {
              return Promise.reject(err)
            })
      } else {
        commit('setWallet', {
          ...wallet,
          accountNumbers: response.data
        })
      }
    } catch (err) {
      return Promise.reject(err)
    }
  },
  fetchNotifications({ commit }, { params }) {
    return new Promise((resolve, reject) => {
      const userId = localStorage.getItem('__user_id')

      axios
        .get(`/users/${userId}/notifications`, { params })
        .then(res => {
          commit('setNotifications', res.data.data)

          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  markNotificationAsRead({ commit, state }, { notificationId }) {
    return new Promise((resolve, reject) => {
      const userId = localStorage.getItem('__user_id')

      axios
        .patch(`/users/${userId}/notifications/${notificationId}`, {
          is_read: 1
        })
        .then(res => {
          commit('updateNotification', { notificationId, data: res.data })

          let count = state.unreadNotificationsCount - 1

          if (count < 0) {
            count = 0
          }

          commit('setUnreadNotificationsCount', count)

          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  fetchUnreadNotificationsCount({ commit }) {
    return new Promise((resolve, reject) => {
      const userId = localStorage.getItem('__user_id')

      axios
        .get(`/users/${userId}/unread-notifications-count`)
        .then(res => {
          commit('setUnreadNotificationsCount', res.data.count)

          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  bookRoute({ commit }, data) {
    return new Promise((resolve, reject) => {
      const userId = localStorage.getItem('__user_id')

      axios
        .post(`/users/${userId}/routes`, data)
        .then(res => {
          commit('addBooking', res.data)

          resolve(res.data)
        })
        .catch(res => {
          reject(res)
        })
    })
  },
  fetchActiveBookings({ commit }) {
    return new Promise((resolve, reject) => {
      const userId = localStorage.getItem('__user_id')

      axios
        .get(`/users/${userId}/routes`)
        .then(res => {
          commit('setActiveBookings', res.data)

          resolve(res.data)
        })
        .catch(res => {
          reject(res)
        })
    })
  },
  fetchPastBookings({ commit }) {
    return new Promise((resolve, reject) => {
      const userId = localStorage.getItem('__user_id')
      axios
        .get(`/users/${userId}/user-route-schedules?status=completed`)
        .then(res => {
          commit('setPastBookings', res.data)
          resolve(res.data)
        })
        .catch(res => {
          reject(res)
        })
    })
  },
  fetchBookings({ dispatch }) {
    return Promise.all([
      dispatch('fetchActiveBookings'),
      dispatch('fetchPastBookings')
    ])
  },
  changeWalletSource({ commit }, walletSource) {
    commit('setWalletSource', walletSource)
  },
  updateLocalBookingData({ commit }, data) {
    commit('setLocalBookingData', data);
  }
}

const mutations = {
  setWalletHistory(state, history) {
    state.walletHistory = history
  },
  setWallet(state, wallet) {
    state.wallet = wallet
  },
  setBusStops(state, data) {
    state.busStops = data
  },
  setSupportedCities(state, data) {
    state.supportedCities = data
  },
  addBooking(state, booking) {
    state.bookings.push(booking)
  },
  setActiveBookings(state, bookings) {
    state.activeBookings = bookings
  },
  setPastBookings(state, bookings) {
    state.pastBookings = bookings
  },
  setNotifications(state, notifications) {
    state.notifications = notifications
  },
  updateNotification(state, { notificationId, data }) {
    const index = state.notifications.findIndex(
      notification => notification.id === notificationId
    )

    if (index !== -1) {
      state.notifications.splice(index, 1, { ...data })
    }
  },
  setUnreadNotificationsCount(state, count) {
    state.unreadNotificationsCount = count
  },
  reset(state) {
    Object.assign(state, defaultState())
  },
  setWalletSource(state, walletSource) {
    state.walletSource = walletSource
  },
  setLocalBookingData(state, bookingData) {
    state.localBookingData = bookingData;
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
